import type { FlowEdge, FlowNode } from '../../types/flow-types';

export interface ReusableSubflow {
	id: string;
	name: string;
	createdAt: number;
	nodes: FlowNode[];
	edges: FlowEdge[];
}

const STORAGE_KEY = 'shelly.reusable-subflows.v1';

function uid(prefix: string): string {
	return typeof crypto !== 'undefined' && crypto.randomUUID
		? `${prefix}-${crypto.randomUUID()}`
		: `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

export function createReusableSubflow(
	name: string,
	allNodes: FlowNode[],
	allEdges: FlowEdge[],
	selectedNodeIds: string[],
): ReusableSubflow {
	const selected = new Set(selectedNodeIds);
	const sourceNodes = allNodes.filter((node) => selected.has(node.id));
	if (!sourceNodes.length) throw new Error('Select at least one node to create a reusable subflow');
	const minX = Math.min(...sourceNodes.map((node) => node.position.x));
	const minY = Math.min(...sourceNodes.map((node) => node.position.y));
	const idMap = new Map(sourceNodes.map((node, index) => [node.id, `node-${index}`]));
	return {
		id: uid('subflow'),
		name: name.trim() || 'Reusable subflow',
		createdAt: Date.now(),
		nodes: sourceNodes.map((node) => ({
			...structuredClone(node),
			id: idMap.get(node.id)!,
			position: { x: node.position.x - minX, y: node.position.y - minY },
		})),
		edges: allEdges
			.filter((edge) => selected.has(edge.source) && selected.has(edge.target))
			.map((edge, index) => ({
				...structuredClone(edge),
				id: `edge-${index}`,
				source: idMap.get(edge.source)!,
				target: idMap.get(edge.target)!,
			})),
	};
}

export function instantiateReusableSubflow(
	subflow: ReusableSubflow,
	origin: { x: number; y: number },
): { nodes: FlowNode[]; edges: FlowEdge[] } {
	const idMap = new Map(subflow.nodes.map((node) => [node.id, uid('node')]));
	return {
		nodes: subflow.nodes.map((node) => ({
			...structuredClone(node),
			id: idMap.get(node.id)!,
			position: { x: node.position.x + origin.x, y: node.position.y + origin.y },
		})),
		edges: subflow.edges.map((edge) => ({
			...structuredClone(edge),
			id: uid('edge'),
			source: idMap.get(edge.source)!,
			target: idMap.get(edge.target)!,
		})),
	};
}

export function loadReusableSubflows(storage: Storage = localStorage): ReusableSubflow[] {
	try {
		const value = JSON.parse(storage.getItem(STORAGE_KEY) || '[]');
		return Array.isArray(value) ? value : [];
	} catch {
		return [];
	}
}

export function saveReusableSubflow(subflow: ReusableSubflow, storage: Storage = localStorage): void {
	const current = loadReusableSubflows(storage).filter((item) => item.id !== subflow.id);
	storage.setItem(STORAGE_KEY, JSON.stringify([...current, subflow]));
}
